"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
Object.defineProperty(exports, "__esModule", { value: true });
var classNames = require("classnames");
var React = require("react");
var Icon_1 = require("../../../../resources/Icon");
var unicode_1 = require("../../../../utils/unicode");
var util_1 = require("../../../../utils/util");
var types_1 = require("../../types");
var InputHelper_1 = require("../InputHelper");
var useDropFileHandlers = function (fileNames, selectFiles, setDragOver, setFileTypeError, multiple, fileTypes) {
    var onChange = React.useCallback(function (e) {
        e.preventDefault();
        selectFiles(e.target.files, multiple);
        // Reset the input value so that a user can re-select a file that might have been removed
        if (e.currentTarget) {
            e.currentTarget.value = "";
        }
    }, [selectFiles, multiple]);
    var onDragEnter = React.useCallback(function (e) {
        e.preventDefault();
        setDragOver(true);
    }, [setDragOver]);
    var onDragLeave = React.useCallback(function (e) {
        e.preventDefault();
        setDragOver(false);
    }, [setDragOver]);
    var onDragOver = React.useCallback(function (e) {
        e.preventDefault();
        // Prevent dropping multiple files if not in multiple mode
        if (!multiple && e.dataTransfer.items.length > 1) {
            e.dataTransfer.dropEffect = "none";
            // Change the pointer to a "+" when files will be appended to the selection
        }
        else if (multiple && fileNames.length > 0) {
            e.dataTransfer.dropEffect = "copy";
        }
    }, [multiple, JSON.stringify(fileNames)]);
    var onDrop = React.useCallback(function (e) {
        e.preventDefault();
        setDragOver(false);
        // Check if the dropped files match the allowed extension, refuse the drop if any doesn't match.
        // This is only required on drag/drop since the html input file does the filtering for us.
        // We have to do this on drop event since the file content is unknown until then (e.g. dragOver only gets mime type,
        // not file name)
        if (fileTypes &&
            fileTypes.extensions &&
            fileTypes.extensions.length > 0) {
            var _loop_1 = function (i) {
                var match = fileTypes.extensions.some(function (ext) {
                    return e.dataTransfer.files[i].name.slice(-ext.length) === ext;
                });
                if (!match) {
                    setFileTypeError(true);
                    return { value: void 0 };
                }
            };
            // Need to use a normal for here since FileList does not support forEach
            for (var i = 0; i < e.dataTransfer.files.length; i++) {
                var state_1 = _loop_1(i);
                if (typeof state_1 === "object")
                    return state_1.value;
            }
        }
        selectFiles(e.dataTransfer.files, multiple);
    }, [
        setDragOver,
        setFileTypeError,
        selectFiles,
        multiple,
        JSON.stringify(fileTypes)
    ]);
    return { onChange: onChange, onDragEnter: onDragEnter, onDragLeave: onDragLeave, onDragOver: onDragOver, onDrop: onDrop };
};
/**
 * OUI drop file selector component.
 *
 * @oui-component DropFileSelector
 */
var DropFileSelector = function (props) {
    var fieldName = props.fieldName, onFilesSelected = props.onFilesSelected, text = props.text, browseLinkText = props.browseLinkText, fileTypes = props.fileTypes, _a = props.multiple, multiple = _a === void 0 ? false : _a, testId = props.testId, label = props.label, labelledBy = props.labelledBy;
    var accept = fileTypes && fileTypes.extensions.join(",");
    var form = React.useContext(types_1.FormContext).form;
    var _b = __read(React.useState(false), 2), dragOver = _b[0], setDragOver = _b[1];
    var _c = __read(React.useState(false), 2), fileTypeError = _c[0], setFileTypeError = _c[1];
    return (React.createElement(InputHelper_1.default, { disabled: false, fieldName: fieldName, onChange: onFilesSelected }, function (helper) {
        var fieldId = helper.fieldId;
        var files = util_1.coerceArray(helper.value || []);
        var fileNames = files.map(function (f) { return f.name; });
        var joinedFileNames = fileNames.length > 0 ? fileNames.join(", ") : null;
        var describedBy = [helper.hintId, helper.errorId].join(" ").trim();
        var selectFiles = React.useCallback(function (fileList, append) {
            if (append === void 0) { append = false; }
            // If we got here, the files passed extensions validation, so we can reset that state
            setFileTypeError(false);
            if (fileList && fileList.length > 0) {
                if (multiple) {
                    var fileListArray = Array.from(fileList);
                    var newFiles = append
                        ? fileListArray.concat(files)
                        : fileListArray;
                    helper.onChange(newFiles);
                }
                else {
                    helper.onChange(fileList[0]);
                }
                helper.onBlur(undefined);
            }
        }, [multiple, JSON.stringify(fileNames)]);
        // I'm not 100% sure this makes sense, but it's copied from the old implementation
        React.useEffect(function () {
            if (fileTypeError && form && helper.fieldName) {
                if (!form.getTouched(helper.fieldName)) {
                    setFileTypeError(false);
                }
            }
        }, [
            fileTypeError,
            form && helper.fieldName && form.getTouched(helper.fieldName)
        ]);
        var _a = useDropFileHandlers(fileNames, selectFiles, setDragOver, setFileTypeError, multiple, fileTypes), onChange = _a.onChange, onDragEnter = _a.onDragEnter, onDragLeave = _a.onDragLeave, onDragOver = _a.onDragOver, onDrop = _a.onDrop;
        var selectedFiles = fileNames.map(function (fileName, idx) {
            var removeFile = function () {
                // Clear error state when removing any files
                setFileTypeError(false);
                if (multiple) {
                    helper.onChange(files.filter(function (_, i) { return i !== idx; }));
                }
                else {
                    helper.onChange(undefined);
                }
            };
            return (React.createElement("div", { className: classNames("oui-margin-small-right", "oui-cursor-default", "oui-display-inline-block", "oui-react-drop-file-selector-selected-file"), key: idx },
                fileName,
                React.createElement("button", { className: "oui-button-small oui-text-top oui-icon-link", "data-oui-icon": "icon: " + Icon_1.default.Close + "; ratio: 0.6", value: idx, onClick: removeFile })));
        });
        return (React.createElement("div", null,
            React.createElement("div", __assign({ className: classNames("oui-placeholder", "oui-text-center", "oui-position-relative", "oui-margin-remove-bottom", {
                    "oui-dragover": dragOver,
                    "oui-react-drop-file-selector-placeholder-error": fileTypeError
                }), onDrop: util_1.preventDefault, onDragOver: util_1.preventDefault, onDragEnd: util_1.preventDefault, onDragEnter: onDragEnter, onDragLeave: util_1.preventDefault, "data-test-id": testId }, util_1.ariaProps({ label: label, labelledBy: labelledBy })),
                React.createElement("span", { "oui-icon": "icon: " + Icon_1.default.CloudUpload }),
                unicode_1.nonBreakingSpace,
                React.createElement("span", { className: "oui-text-middle" }, text),
                React.createElement("div", { "data-oui-form-custom": "" },
                    React.createElement("input", __assign({ type: "file", accept: accept, onChange: onChange, multiple: multiple, title: joinedFileNames, id: fieldId }, util_1.ariaProps({ describedBy: describedBy }))),
                    unicode_1.nonBreakingSpace,
                    React.createElement("span", { className: "oui-link" }, browseLinkText)),
                React.createElement("div", null,
                    React.createElement("em", { className: classNames("oui-margin-small-top", "oui-text-small", { "oui-form-danger": fileTypeError }) }, fileTypes && fileTypes.requirementsText)),
                // on children elements
                dragOver && (React.createElement("div", { className: "oui-position-cover", onDrop: onDrop, onDragOver: onDragOver, onDragEnd: util_1.preventDefault, onDragEnter: util_1.preventDefault, onDragLeave: onDragLeave }))),
            fileTypeError &&
                React.createElement("div", { className: "oui-text-reserve oui-text-small oui-form-danger", role: "alert" }, fileTypes.errorText + " " + fileTypes.requirementsText),
            selectedFiles.length > 0 &&
                React.createElement("div", { className: "oui-margin-top", "data-oui-margin": "" }, selectedFiles)));
    }));
};
exports.default = DropFileSelector;
//# sourceMappingURL=DropFileSelector.js.map